import { useState, useRef, useEffect } from 'react';
import {
  Send,
  Mic,
  MicOff,
  Loader2,
  Trash2,
  Plus,
  ChevronLeft,
  MessageSquare,
  Bot,
  Camera,
  X,
  Image as ImageIcon,
  FileText,
  GripVertical,
  Maximize2,
  Minimize2,
  Download,
  Sparkles,
  Search
} from '../../icons/wp-icons';
import { chatApi } from '../../api/chatApi';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useFirmendaten } from '../../hooks/useFirmendaten';
import { useAuth } from '../../context/AuthContext';
import MessageDraft from './MessageDraft';
import { ChatActions } from './ChatActions';
import { processToolResults } from '../../utils/dataRefreshEvents';
import SignaturePad from '../Unterschrift/SignaturePad';
import { unterschriftApi } from '../../api/unterschriftApi';

export default function ChatWidget({ onClose, isOpen = true }) {
  const isMobile = useIsMobile();
  const { firmendaten } = useFirmendaten();
  const { user } = useAuth();
  const firmenname = firmendaten?.firmenname || 'WerkPilot';
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // localStorage Key pro User damit Org-/User-Wechsel keine stale IDs erzeugt
  const storageKey = user?.id ? `chatLastKonversationId_${user.id}` : 'chatLastKonversationId';
  const [konversationId, setKonversationIdRaw] = useState(() => {
    // Migration: alten globalen Key löschen falls vorhanden
    const oldKey = localStorage.getItem('chatLastKonversationId');
    if (oldKey) {
      localStorage.removeItem('chatLastKonversationId');
    }
    return localStorage.getItem(storageKey) || null;
  });
  const setKonversationId = (id) => {
    setKonversationIdRaw(id);
    if (id) {
      localStorage.setItem(storageKey, id);
    } else {
      localStorage.removeItem(storageKey);
    }
  };
  const [showHistory, setShowHistory] = useState(false);
  const [konversationen, setKonversationen] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState('');
  const [speechError, setSpeechError] = useState(null);
  const recognitionRef = useRef(null);
  const pendingVoiceSendRef = useRef(false);
  const silenceTimerRef = useRef(null);

  // Bild-Upload State
  const [selectedImage, setSelectedImage] = useState(null); // { file, preview }
  const [uploadedImageId, setUploadedImageId] = useState(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [imageError, setImageError] = useState(null);
  const imageInputRef = useRef(null);
  const [loadingText, setLoadingText] = useState('Denke nach...');
  const [lightboxImage, setLightboxImage] = useState(null);
  const [showChatSignature, setShowChatSignature] = useState(null); // { anfrageId }
  const [pendingSignatureMessage, setPendingSignatureMessage] = useState(null);
  const lastAnfrageIdRef = useRef(null); // Fallback anfrageId from CHAT_ACTIONS

  // Suche State
  const [suchQuery, setSuchQuery] = useState('');
  const [suchErgebnisse, setSuchErgebnisse] = useState(null);
  const [istSuche, setIstSuche] = useState(false);
  const suchTimerRef = useRef(null);

  // Bild-Download für Mobile/PWA (download-Attribut funktioniert dort nicht)
  const handleDownloadImage = async (imageUrl, filename = 'bild.jpg') => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();

      // Mobile: Web Share API nutzen (öffnet Share-Sheet zum Speichern)
      if (isMobile && navigator.share) {
        const file = new File([blob], filename, { type: blob.type || 'image/jpeg' });
        if (navigator.canShare?.({ files: [file] })) {
          await navigator.share({ files: [file], title: filename });
          return;
        }
      }

      // Fallback: Blob-URL-Download (funktioniert auch in Desktop-Browsern)
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Cleanup nach kurzem Delay damit der Download starten kann
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (err) {
      console.error('Download fehlgeschlagen:', err);
      window.open(imageUrl, '_blank');
    }
  };

  // Resize State (für Desktop)
  const [chatSize, setChatSize] = useState(() => {
    const saved = localStorage.getItem('chatWidgetSize');
    return saved ? JSON.parse(saved) : { width: 420, height: 600 };
  });
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState(null);
  const chatRef = useRef(null);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Restore last conversation on first mount
  const hasLoadedRef = useRef(false);
  useEffect(() => {
    if (!hasLoadedRef.current && konversationId && messages.length === 0) {
      hasLoadedRef.current = true;
      loadKonversation(konversationId).catch(() => {
        setKonversationId(null);
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
      // Auto-scroll to latest message
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
    }
  }, [isOpen]);

  // Disable body scroll on mobile when chat is open
  useEffect(() => {
    if (isMobile && isOpen) {
      // Save current scroll position and disable scroll
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';

      return () => {
        // Re-enable scroll and restore position
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isMobile, isOpen]);

  // Browser-Zurück-Geste auf Mobile: Chat schließen statt Seite zurück
  useEffect(() => {
    if (!isMobile || !isOpen) return;

    // History-Eintrag hinzufügen, damit "Zurück" den Chat schließt
    window.history.pushState({ chatOpen: true }, '');

    const handlePopState = (e) => {
      // Wenn der User "zurück" drückt und der Chat offen ist: schließen
      onClose();
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      // History-Eintrag entfernen wenn Chat programmatisch geschlossen wird
      if (window.history.state?.chatOpen) {
        window.history.back();
      }
    };
  }, [isMobile, isOpen, onClose]);

  // Check if Speech Recognition is supported
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      setSpeechSupported(true);
    }
  }, []);

  // Lightbox: Escape-Key + Body-Scroll-Lock
  useEffect(() => {
    if (!lightboxImage) return;
    const handleKeyDown = (e) => { if (e.key === 'Escape') setLightboxImage(null); };
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', handleKeyDown); document.body.style.overflow = ''; };
  }, [lightboxImage]);

  // Resize Handler für Desktop
  useEffect(() => {
    if (isMobile || !isResizing) return;

    const handleMouseMove = (e) => {
      if (!chatRef.current) return;

      const rect = chatRef.current.getBoundingClientRect();
      let newWidth = chatSize.width;
      let newHeight = chatSize.height;

      if (resizeDirection?.includes('left')) {
        newWidth = Math.max(320, Math.min(800, rect.right - e.clientX));
      }
      if (resizeDirection?.includes('top')) {
        newHeight = Math.max(400, Math.min(window.innerHeight - 150, rect.bottom - e.clientY));
      }

      setChatSize({ width: newWidth, height: newHeight });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      setResizeDirection(null);
      // Save to localStorage using current values from DOM
      if (chatRef.current) {
        const rect = chatRef.current.getBoundingClientRect();
        const savedSize = { width: Math.round(rect.width), height: Math.round(rect.height) };
        localStorage.setItem('chatWidgetSize', JSON.stringify(savedSize));
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isMobile, isResizing, resizeDirection, chatSize]);

  // Start Resize
  const startResize = (direction) => (e) => {
    e.preventDefault();
    setIsResizing(true);
    setResizeDirection(direction);
  };

  // Reset Size
  const resetSize = () => {
    const defaultSize = { width: 420, height: 600 };
    setChatSize(defaultSize);
    localStorage.setItem('chatWidgetSize', JSON.stringify(defaultSize));
  };

  // Load conversations when showing history
  useEffect(() => {
    if (showHistory) {
      loadKonversationen();
    }
  }, [showHistory]);

  const loadKonversationen = async () => {
    try {
      const data = await chatApi.getKonversationen();
      setKonversationen(data);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    }
  };

  const loadKonversation = async (id) => {
    try {
      const data = await chatApi.getKonversation(id);
      const loadedMessages = data.nachrichten.map(n => ({
        id: n.id,
        role: n.rolle,
        content: n.inhalt,
        // Thumbnails aus DB laden (Geister-Bilder für Historie)
        imageThumbnails: n.imageThumbnails || null,
        // KI-generiertes Bild aus Tool-Ergebnissen
        generatedImageUrl: n.generatedImageUrl || null
      }));

      // Wenn die letzte Nachricht vom User ist, wurde die KI-Antwort unterbrochen
      // → Automatisch erneut senden statt nur Warnung anzuzeigen
      const lastMsg = loadedMessages[loadedMessages.length - 1];
      const shouldRetry = lastMsg && lastMsg.role === 'user';

      setMessages(loadedMessages);
      setKonversationId(id);
      setShowHistory(false);

      if (shouldRetry) {
        // Letzte User-Nachricht automatisch erneut an die KI senden
        const retryContent = lastMsg.content;
        setIsLoading(true);
        setLoadingText('Anfrage wird fortgesetzt...');
        try {
          const response = await chatApi.sendMessage(retryContent, id, null);
          if (response.konversationId) {
            setKonversationId(response.konversationId);
          }
          const assistantMessage = {
            id: Date.now() + 1,
            role: 'assistant',
            content: response.message,
            generatedImageUrl: response.generatedImageUrl || null
          };
          setMessages(prev => [...prev, assistantMessage]);
          if (response.toolResults && response.toolResults.length > 0) {
            processToolResults(response.toolResults);
          }
        } catch (error) {
          console.error('Retry error:', error);
          setMessages(prev => [...prev, {
            id: 'retry-error-' + Date.now(),
            role: 'assistant',
            content: '⚠️ Die letzte Anfrage konnte nicht automatisch fortgesetzt werden. Bitte senden Sie Ihre Anfrage erneut.'
          }]);
        } finally {
          setIsLoading(false);
        }
      }
    } catch (error) {
      console.error('Failed to load conversation:', error);
    }
  };

  const deleteKonversation = async (id, e) => {
    e.stopPropagation();
    try {
      await chatApi.deleteKonversation(id);
      setKonversationen(prev => prev.filter(k => k.id !== id));
      if (konversationId === id) {
        startNewConversation();
      }
    } catch (error) {
      console.error('Failed to delete conversation:', error);
    }
  };

  const startNewConversation = () => {
    setMessages([]);
    setKonversationId(null);
    setShowHistory(false);
    clearSelectedImage();
    inputRef.current?.focus();
  };

  // Suche mit Debounce
  const handleSuchChange = (value) => {
    setSuchQuery(value);
    if (suchTimerRef.current) clearTimeout(suchTimerRef.current);
    if (!value.trim() || value.trim().length < 2) {
      setSuchErgebnisse(null);
      setIstSuche(false);
      return;
    }
    setIstSuche(true);
    suchTimerRef.current = setTimeout(async () => {
      try {
        const ergebnisse = await chatApi.sucheKonversationen(value.trim());
        setSuchErgebnisse(ergebnisse);
      } catch (error) {
        console.error('Suche fehlgeschlagen:', error);
        setSuchErgebnisse([]);
      } finally {
        setIstSuche(false);
      }
    }, 300);
  };

  // Alle Konversationen löschen
  const handleAlleLoeschen = async () => {
    if (!window.confirm('Möchten Sie wirklich ALLE Chats unwiderruflich löschen?')) return;
    try {
      await chatApi.deleteAlleKonversationen();
      setKonversationen([]);
      setSuchErgebnisse(null);
      setSuchQuery('');
      startNewConversation();
    } catch (error) {
      console.error('Alle löschen fehlgeschlagen:', error);
    }
  };

  // Datei-Handler - gemeinsame Upload-Logik für Bilder und PDFs
  const processImageFile = async (file) => {
    // Validierung - Bilder und PDFs erlaubt
    const allowedImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif'];
    const allowedPdfTypes = ['application/pdf'];
    const allowedTypes = [...allowedImageTypes, ...allowedPdfTypes];

    const isPdf = allowedPdfTypes.includes(file.type);

    if (!allowedTypes.includes(file.type)) {
      setImageError('Nur Bilder (JPG, PNG, GIF, WebP, HEIC) oder PDFs sind erlaubt.');
      return;
    }

    // Größen-Limits: 10MB für Bilder, 50MB für PDFs
    const maxSize = isPdf ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setImageError(isPdf ? 'PDF darf maximal 50MB groß sein.' : 'Bild darf maximal 10MB groß sein.');
      return;
    }

    setImageError(null);

    // Preview für Bilder, Icon-Placeholder für PDFs
    const preview = isPdf ? null : URL.createObjectURL(file);
    setSelectedImage({
      file,
      preview,
      isPdf,
      filename: file.name
    });
    setIsUploadingImage(true);

    try {
      const result = await chatApi.uploadImage(file);
      setUploadedImageId(result.id);
    } catch (error) {
      console.error('File upload failed:', error);
      setImageError('Upload fehlgeschlagen. Bitte erneut versuchen.');
      // Nur ID zurücksetzen, Vorschau bleibt sichtbar für Retry
      setUploadedImageId(null);
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await processImageFile(file);
  };

  // Zwischenablage-Paste Handler
  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) {
          await processImageFile(file);
        }
        return;
      }
    }
  };

  const clearSelectedImage = () => {
    if (selectedImage?.preview) {
      URL.revokeObjectURL(selectedImage.preview);
    }
    setSelectedImage(null);
    setUploadedImageId(null);
    setImageError(null);
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  };

  const openImagePicker = () => {
    imageInputRef.current?.click();
  };

  // Cleanup image preview URL on unmount
  useEffect(() => {
    return () => {
      if (selectedImage?.preview) {
        URL.revokeObjectURL(selectedImage.preview);
      }
    };
  }, [selectedImage]);

  // Clear image error after 3 seconds
  useEffect(() => {
    if (imageError) {
      const timer = setTimeout(() => setImageError(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [imageError]);

  // Cleanup recognition on unmount
  useEffect(() => {
    return () => {
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);

  // Clear speech error after 3 seconds
  useEffect(() => {
    if (speechError) {
      const timer = setTimeout(() => setSpeechError(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [speechError]);

  // Auto-send message after signature save
  useEffect(() => {
    if (pendingSignatureMessage && !isLoading) {
      setInputValue(pendingSignatureMessage);
      setPendingSignatureMessage(null);
      // Wait for state to settle, then trigger send
      setTimeout(() => {
        const sendBtn = document.querySelector('[data-chat-send]');
        if (sendBtn && !sendBtn.disabled) sendBtn.click();
      }, 150);
    }
  }, [pendingSignatureMessage, isLoading]);

  const stopRecording = () => {
    if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setIsRecording(false);
    setInterimTranscript('');
  };

  const startRecording = async () => {
    if (!speechSupported) {
      setSpeechError('Spracherkennung wird von diesem Browser nicht unterstützt.');
      return;
    }

    // Clear any previous errors
    setSpeechError(null);
    setInterimTranscript('');

    // Create new recognition instance each time
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognitionInstance = new SpeechRecognition();

    // Enable interim results for live feedback
    recognitionInstance.continuous = false;
    recognitionInstance.interimResults = true;
    recognitionInstance.lang = 'de-DE';
    recognitionInstance.maxAlternatives = 1;

    // Silence-Timer helper: stoppt Aufnahme automatisch nach Stille
    const resetSilenceTimer = () => {
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = setTimeout(() => {
        // Keine Sprache mehr erkannt → Aufnahme beenden
        if (recognitionRef.current) {
          recognitionRef.current.stop();
        }
      }, 2500); // 2.5s Stille → auto-stop
    };

    recognitionInstance.onstart = () => {
      setIsRecording(true);
      setSpeechError(null);
      // Starte initialen Silence-Timer (falls User gar nicht spricht)
      resetSilenceTimer();
    };

    recognitionInstance.onresult = (event) => {
      let interim = '';
      let final = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          final += transcript;
        } else {
          interim += transcript;
        }
      }

      // Show interim results in real-time
      setInterimTranscript(interim);

      if (final) {
        // Finales Ergebnis → Timer stoppen, Aufnahme aktiv beenden
        if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
        pendingVoiceSendRef.current = true;
        setInputValue(prev => {
          const newValue = prev ? prev + ' ' + final : final;
          return newValue.trim();
        });
        setInterimTranscript('');
        // iOS/WebKit beendet bei continuous=false nicht immer zuverlässig → aktiv stoppen
        if (recognitionRef.current) {
          recognitionRef.current.stop();
        }
      } else if (interim) {
        // User spricht noch → Timer zurücksetzen
        resetSilenceTimer();
      }
    };

    recognitionInstance.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
      setIsRecording(false);
      setInterimTranscript('');

      // User-friendly error messages
      switch (event.error) {
        case 'not-allowed':
        case 'permission-denied':
          setSpeechError('Mikrofon-Zugriff wurde verweigert. Bitte erlauben Sie den Zugriff in den Browser-Einstellungen.');
          break;
        case 'no-speech':
          setSpeechError('Keine Sprache erkannt. Bitte sprechen Sie deutlicher.');
          break;
        case 'audio-capture':
          setSpeechError('Kein Mikrofon gefunden. Bitte schließen Sie ein Mikrofon an.');
          break;
        case 'network':
          setSpeechError('Netzwerkfehler. Bitte prüfen Sie Ihre Internetverbindung.');
          break;
        case 'aborted':
          // User cancelled, no error message needed
          break;
        default:
          setSpeechError('Fehler bei der Spracherkennung. Bitte versuchen Sie es erneut.');
      }
    };

    recognitionInstance.onend = () => {
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
      setIsRecording(false);
      setInterimTranscript('');
    };

    recognitionRef.current = recognitionInstance;

    try {
      recognitionInstance.start();
    } catch (error) {
      console.error('Failed to start recognition:', error);
      setSpeechError('Spracherkennung konnte nicht gestartet werden.');
      setIsRecording(false);
    }
  };

  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const sendMessage = async () => {
    const message = inputValue.trim();
    const hasImage = uploadedImageId && selectedImage;

    // Mindestens Text oder Bild erforderlich
    if ((!message && !hasImage) || isLoading || isUploadingImage) return;

    // Bild-Daten für User-Message speichern
    const imagePreviewUrl = selectedImage?.preview;
    const currentImageId = uploadedImageId;

    // Add user message
    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: message || '📷 Bild gesendet',
      imageUrl: imagePreviewUrl // Für Anzeige im Chat
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');

    // Bild-State zurücksetzen (aber Preview-URL behalten für Anzeige)
    setSelectedImage(null);
    setUploadedImageId(null);
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }

    // Bild dabei → immer Vision AI (Analyse oder Bearbeitung)
    setLoadingText(currentImageId ? 'Vision AI lädt...' : 'Denke nach...');

    setIsLoading(true);

    try {
      // imageIds als Array senden wenn vorhanden
      const imageIds = currentImageId ? [currentImageId] : null;
      let response;

      try {
        response = await chatApi.sendMessage(message, konversationId, imageIds);
      } catch (sendError) {
        // 404 = Konversation gehört zu anderer Org/User → neue Konversation starten
        if (sendError?.response?.status === 404 && konversationId) {
          console.warn('Konversation nicht gefunden, starte neue Konversation');
          setKonversationId(null);
          response = await chatApi.sendMessage(message, null, imageIds);
        } else {
          throw sendError;
        }
      }

      // Update conversation ID if new
      if (response.konversationId) {
        setKonversationId(response.konversationId);
      }

      // Add assistant message
      const assistantMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: response.message,
        generatedImageUrl: response.generatedImageUrl || null
      };
      setMessages(prev => [...prev, assistantMessage]);

      // Verarbeite Tool-Results für Echtzeit-Updates in anderen Komponenten
      if (response.toolResults && response.toolResults.length > 0) {
        processToolResults(response.toolResults);
      }

    } catch (error) {
      console.error('Chat error:', error?.response?.status, error?.response?.data || error?.message);
      let errorText = 'Entschuldigung, es gab einen Fehler. Bitte versuchen Sie es erneut.';
      if (error?.response?.status === 429) {
        errorText = 'Zu viele Anfragen. Bitte warten Sie einen Moment und versuchen Sie es erneut.';
      } else if (error?.code === 'ERR_NETWORK' || !error?.response) {
        errorText = 'Verbindungsfehler. Bitte prüfen Sie Ihre Internetverbindung.';
      }
      const errorMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: errorText
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  // Auto-send nach Spracherkennung
  useEffect(() => {
    if (pendingVoiceSendRef.current && inputValue.trim() && !isLoading && !isRecording) {
      pendingVoiceSendRef.current = false;
      sendMessage();
    }
  }, [inputValue, isRecording]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Handle Tool-Action from ChatActions component
  const handleToolAction = async (toolName, params) => {
    const actionMessage = `[TOOL_ACTION:${toolName}:${JSON.stringify(params)}]`;

    // Add user message indicating the action
    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: `🔧 ${getActionLabel(toolName, params)}`
    };
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    try {
      let response;
      try {
        response = await chatApi.sendMessage(actionMessage, konversationId, null);
      } catch (sendError) {
        if (sendError?.response?.status === 404 && konversationId) {
          setKonversationId(null);
          response = await chatApi.sendMessage(actionMessage, null, null);
        } else {
          throw sendError;
        }
      }

      if (response.konversationId) {
        setKonversationId(response.konversationId);
      }

      const assistantMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: response.message,
        generatedImageUrl: response.generatedImageUrl || null
      };
      setMessages(prev => [...prev, assistantMessage]);

      if (response.toolResults && response.toolResults.length > 0) {
        processToolResults(response.toolResults);
      }
    } catch (error) {
      console.error('Tool action error:', error?.response?.status, error?.response?.data || error?.message);
      let errorText = 'Entschuldigung, die Aktion konnte nicht ausgeführt werden.';
      if (error?.response?.status === 429) {
        errorText = 'Zu viele Anfragen. Bitte warten Sie einen Moment.';
      } else if (error?.code === 'ERR_NETWORK' || !error?.response) {
        errorText = 'Verbindungsfehler. Bitte prüfen Sie Ihre Internetverbindung.';
      }
      const errorMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: errorText
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Get human-readable label for tool action
  const getActionLabel = (toolName, params) => {
    const labels = {
      'kva_pdf_versenden': 'KVA per E-Mail versenden',
      'rechnung_pdf_versenden': 'Rechnung per E-Mail versenden',
      'kva_in_rechnung_umwandeln': 'KVA in Rechnung umwandeln',
      'termin_erstellen': 'Termin anlegen',
      'termin_erinnerung_senden': 'Terminerinnerung senden',
      'kva_smart_erstellen': 'Kostenvoranschlag erstellen',
      'rechnung_smart_erstellen': 'Rechnung erstellen'
    };
    return labels[toolName] || toolName;
  };

  // Handle quick reply from ChatActions
  const handleQuickReply = (message) => {
    setInputValue(message);
    inputRef.current?.focus();
  };

  // Handle Signature from Chat
  const handleChatSignatureSave = async (signatureData) => {
    if (!showChatSignature) return;

    if (!showChatSignature.anfrageId) {
      alert('Fehler: Keine Anfrage-ID vorhanden. Bitte starten Sie den Unterschrift-Vorgang erneut über den Chat.');
      return;
    }

    try {
      const result = await unterschriftApi.create({
        anfrageId: showChatSignature.anfrageId,
        signatureData: signatureData.signatureData,
        signiertVon: signatureData.signiertVon,
        typ: signatureData.typ || 'Abnahme',
        notizen: signatureData.notizen || ''
      });

      const savedAnfrageId = showChatSignature.anfrageId;
      const savedUnterschriftId = result.unterschrift?.id || result.id;
      setShowChatSignature(null);

      // Bestätigung als Chat-Nachricht senden + Arbeitsnachweis generieren
      setPendingSignatureMessage(`Unterschrift gespeichert. Erstelle jetzt den Arbeitsnachweis für Anfrage ${savedAnfrageId} mit Unterschrift ${savedUnterschriftId}`);
    } catch (error) {
      console.error('Unterschrift-Fehler:', error, 'AnfrageId:', showChatSignature?.anfrageId);
      const serverError = error.response?.data?.error;
      alert('Fehler beim Speichern der Unterschrift: ' + (serverError || error.message || 'Unbekannter Fehler'));
    }
  };

  // Format message content (simple markdown-like)
  const formatContent = (content) => {
    // Safety-Net: Bild-Links entfernen (Bild wird bereits als Thumbnail angezeigt)
    content = content
      .replace(/\[([^\]]*)\]\(sandbox:[^)]*\)/g, '')
      .replace(/\[([^\]]*)\]\([^)]*\/api\/chat\/public\/images\/[^)]*\)/g, '')
      .replace(/\n{3,}/g, '\n\n').trim() || content;

    // Prüfe ob es ein MESSAGE_DRAFT JSON ist
    if (content.includes('"type":"MESSAGE_DRAFT"') || content.includes('"type": "MESSAGE_DRAFT"')) {
      try {
        // Extrahiere JSON aus dem Content
        const jsonMatch = content.match(/\{[\s\S]*"type"\s*:\s*"MESSAGE_DRAFT"[\s\S]*\}/);
        if (jsonMatch) {
          const parsed = JSON.parse(jsonMatch[0]);
          if (parsed.type === 'MESSAGE_DRAFT' && parsed.data) {
            // Extrahiere Text vor dem JSON
            const textBefore = content.substring(0, content.indexOf(jsonMatch[0])).trim();
            return (
              <div>
                {textBefore && <p className="mb-3">{textBefore}</p>}
                <MessageDraft data={parsed.data} />
              </div>
            );
          }
        }
      } catch (e) {
        // Falls JSON-Parsing fehlschlägt, normal formatieren
        console.debug('MessageDraft parsing failed, using normal format');
      }
    }

    // Prüfe ob es ein CHAT_ACTIONS JSON ist
    if (content.includes('"type":"CHAT_ACTIONS"') || content.includes('"type": "CHAT_ACTIONS"')) {
      try {
        const jsonMatch = content.match(/\{[\s\S]*"type"\s*:\s*"CHAT_ACTIONS"[\s\S]*\}/);
        if (jsonMatch) {
          const parsed = JSON.parse(jsonMatch[0]);
          if (parsed.type === 'CHAT_ACTIONS') {
            // AnfrageId aus signatureAction oder quickReply-Text extrahieren und speichern
            const uuidPattern = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
            const sigQr = parsed.quickReplies?.find(qr => qr.signatureAction?.anfrageId);
            if (sigQr) {
              lastAnfrageIdRef.current = sigQr.signatureAction.anfrageId;
            } else {
              // Fallback: UUID aus quickReply-Message-Text extrahieren (alte CHAT_ACTIONS ohne signatureAction)
              const unterschriftQr = parsed.quickReplies?.find(qr => qr.label?.includes('Unterschrift') || qr.message?.includes('unterschreiben'));
              const uuidMatch = unterschriftQr?.message?.match(uuidPattern);
              if (uuidMatch) {
                lastAnfrageIdRef.current = uuidMatch[0];
              }
            }
            const textBefore = content.substring(0, content.indexOf(jsonMatch[0])).trim();
            return (
              <div>
                {textBefore && <p className="mb-3">{textBefore}</p>}
                <ChatActions
                  data={parsed}
                  onAction={handleToolAction}
                  onQuickReply={handleQuickReply}
                  onSignature={({ anfrageId }) => setShowChatSignature({ anfrageId })}
                />
              </div>
            );
          }
        }
      } catch (e) {
        console.debug('ChatActions parsing failed, using normal format');
      }
    }

    // Prüfe ob es ein CHAT_SIGNATURE JSON ist
    if (content.includes('"type":"CHAT_SIGNATURE"') || content.includes('"type": "CHAT_SIGNATURE"')) {
      try {
        const jsonMatch = content.match(/\{[\s\S]*"type"\s*:\s*"CHAT_SIGNATURE"[\s\S]*\}/);
        if (jsonMatch) {
          const parsed = JSON.parse(jsonMatch[0]);
          if (parsed.type === 'CHAT_SIGNATURE') {
            // Verwende anfrageId aus CHAT_SIGNATURE, Fallback auf gespeicherte ID aus CHAT_ACTIONS
            const isValidUUID = (id) => id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
            let anfrageId = isValidUUID(parsed.anfrageId) ? parsed.anfrageId : lastAnfrageIdRef.current;
            // Letzter Fallback: UUID aus dem gesamten Content extrahieren (z.B. aus vorherigem Text)
            if (!anfrageId) {
              const uuidFallback = content.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i);
              if (uuidFallback) anfrageId = uuidFallback[0];
            }
            if (!anfrageId) {
              console.error('CHAT_SIGNATURE: Keine gültige anfrageId gefunden', { parsed: parsed.anfrageId, fallback: lastAnfrageIdRef.current });
            }
            const textBefore = content.substring(0, content.indexOf(jsonMatch[0])).trim();
            return (
              <div>
                {textBefore && <p className="mb-3">{textBefore}</p>}
                <p className="mb-3 font-medium">{parsed.text || 'Bitte unterschreiben Sie:'}</p>
                <div className="flex flex-col gap-3 mt-4">
                  <button
                    onClick={() => setShowChatSignature({ anfrageId })}
                    className="w-full flex items-center justify-center gap-2 px-5 py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-150 text-base"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                    Jetzt unterschreiben
                  </button>
                  <button
                    onClick={() => {
                      setInputValue(`Erstelle einen Arbeitsnachweis für Anfrage ${anfrageId} ohne Unterschrift`);
                      setTimeout(() => inputRef.current?.focus(), 50);
                    }}
                    className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-white hover:bg-gray-50 text-gray-700 font-medium rounded-xl border-2 border-gray-200 hover:border-gray-300 transition-all duration-150 text-base"
                  >
                    Ohne Unterschrift
                  </button>
                </div>
              </div>
            );
          }
        }
      } catch (e) {
        console.debug('CHAT_SIGNATURE parsing failed');
      }
    }

    // Prüfe ob Markdown-Tabelle enthalten ist und konvertiere zu HTML
    const tableRegex = /(\|[^\n]+\|\n\|[-:\s|]+\|\n(?:\|[^\n]+\|\n?)+)/g;
    const hasTable = tableRegex.test(content);

    if (hasTable) {
      // Split content by tables
      const segments = content.split(tableRegex);
      tableRegex.lastIndex = 0; // Reset regex
      const tables = content.match(tableRegex) || [];

      return segments.map((segment, segIndex) => {
        // Check if this segment is followed by a table
        const tableIndex = Math.floor(segIndex / 2);
        const isBeforeTable = segIndex % 2 === 0;

        const elements = [];

        // Render text segment
        if (segment.trim()) {
          elements.push(
            <span key={`text-${segIndex}`}>
              {segment.split('\n').map((line, i) => (
                <span key={i}>
                  {formatLine(line)}
                  {i < segment.split('\n').length - 1 && <br />}
                </span>
              ))}
            </span>
          );
        }

        // Render table if this is the position
        if (isBeforeTable && tables[tableIndex]) {
          elements.push(renderMarkdownTable(tables[tableIndex], `table-${tableIndex}`));
        }

        return elements;
      });
    }

    // Split by code blocks and regular text
    const parts = content.split(/(```[\s\S]*?```)/g);

    return parts.map((part, index) => {
      if (part.startsWith('```')) {
        // Code block
        const code = part.replace(/```\w*\n?/g, '').replace(/```$/g, '');
        return (
          <pre key={index} className="bg-gray-100 rounded p-2 my-2 overflow-x-auto text-sm">
            <code>{code}</code>
          </pre>
        );
      }

      // Regular text - handle line breaks, bold, and links
      return (
        <span key={index}>
          {part.split('\n').map((line, i) => (
            <span key={i}>
              {formatLine(line)}
              {i < part.split('\n').length - 1 && <br />}
            </span>
          ))}
        </span>
      );
    });
  };

  // Markdown-Tabelle zu HTML konvertieren
  const renderMarkdownTable = (tableStr, key) => {
    const lines = tableStr.trim().split('\n').filter(l => l.trim());
    if (lines.length < 2) return null;

    // Header
    const headerCells = lines[0].split('|').filter(c => c.trim()).map(c => c.trim());
    // Skip separator line (index 1)
    // Body rows
    const bodyRows = lines.slice(2).map(line =>
      line.split('|').filter(c => c.trim()).map(c => c.trim())
    );

    return (
      <div key={key} className="my-3 overflow-x-auto">
        <table className="w-full text-xs border-collapse bg-white rounded-lg overflow-hidden shadow-sm">
          <thead>
            <tr className="bg-gray-100">
              {headerCells.map((cell, i) => (
                <th key={i} className="px-3 py-2 text-left font-semibold text-gray-700 border-b border-gray-200">
                  {cell}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {bodyRows.map((row, rowIndex) => (
              <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                {row.map((cell, cellIndex) => (
                  <td key={cellIndex} className="px-3 py-2 text-gray-600 border-b border-gray-100">
                    {formatLine(cell)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Format a single line with bold and links
  const formatLine = (line) => {
    // First handle Markdown links: [text](url)
    const markdownLinkPattern = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g;
    // Plain URL pattern
    const urlPattern = /(https?:\/\/[^\s<>[\]()]+)/g;
    // Bold pattern
    const boldPattern = /(\*\*.*?\*\*)/g;

    // Split by markdown links first
    const parts = [];
    let lastIndex = 0;
    let match;

    // Find all markdown links
    while ((match = markdownLinkPattern.exec(line)) !== null) {
      // Add text before this match
      if (match.index > lastIndex) {
        parts.push({ type: 'text', content: line.slice(lastIndex, match.index) });
      }
      // Add the markdown link
      parts.push({ type: 'mdlink', text: match[1], url: match[2] });
      lastIndex = match.index + match[0].length;
    }
    // Add remaining text
    if (lastIndex < line.length) {
      parts.push({ type: 'text', content: line.slice(lastIndex) });
    }

    // If no markdown links found, treat whole line as text
    if (parts.length === 0) {
      parts.push({ type: 'text', content: line });
    }

    return parts.map((part, i) => {
      if (part.type === 'mdlink') {
        return renderLink(part.url, part.text, i);
      }

      // For text parts, handle plain URLs and bold
      const textContent = part.content;
      const urlParts = textContent.split(urlPattern);

      return urlParts.map((segment, j) => {
        // Check if this segment is a URL
        if (urlPattern.test(segment)) {
          urlPattern.lastIndex = 0;
          return renderLink(segment, null, `${i}-${j}`);
        }

        // Handle bold in non-URL parts
        return segment.split(boldPattern).map((boldSegment, k) => {
          if (boldSegment.startsWith('**') && boldSegment.endsWith('**')) {
            return <strong key={`${i}-${j}-${k}`}>{boldSegment.slice(2, -2)}</strong>;
          }
          return <span key={`${i}-${j}-${k}`}>{boldSegment}</span>;
        });
      });
    });
  };

  // Render a link (internal or external)
  const renderLink = (url, displayText, key) => {
    const currentOrigin = window.location.origin;
    const isInternalLink = url.startsWith(currentOrigin) || url.startsWith('http://localhost');

    // Determine display text
    const linkText = displayText || extractLinkText(url);

    if (isInternalLink) {
      // Internal link - navigate in same tab to keep auth
      const path = url.replace(currentOrigin, '').replace(/^https?:\/\/localhost:\d+/, '');
      return (
        <a
          key={key}
          href={path}
          className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors font-medium text-sm"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            window.location.href = path;
          }}
        >
          {linkText}
        </a>
      );
    }

    // External link - open in new tab
    return (
      <a
        key={key}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:text-blue-800 underline break-all"
        onClick={(e) => e.stopPropagation()}
      >
        {linkText}
      </a>
    );
  };

  // Extract a nice link text from internal URLs
  const extractLinkText = (url) => {
    try {
      const path = new URL(url).pathname;

      // Match patterns and return friendly names
      if (path.includes('/rechnungen/') && path.includes('/bearbeiten')) {
        return '📄 Rechnung öffnen';
      }
      if (path.includes('/kostenvoranschlaege/') && path.includes('/bearbeiten')) {
        return '📋 KVA öffnen';
      }
      if (path.includes('/kunden/')) {
        return '👤 Kunde öffnen';
      }
      if (path.includes('/termine/')) {
        return '📅 Termin öffnen';
      }

      // Default: show shortened path
      return path.length > 30 ? path.substring(0, 30) + '...' : path;
    } catch {
      return url.length > 50 ? url.substring(0, 50) + '...' : url;
    }
  };

  return (
    <div
      ref={chatRef}
      className={`fixed z-50 bg-white flex flex-col overflow-hidden ${
        isMobile
          ? 'inset-0 rounded-none'
          : 'right-6 rounded-2xl shadow-2xl border border-gray-200'
      } ${isResizing ? 'select-none' : ''} ${!isOpen ? 'hidden' : ''}`}
      style={isMobile ? {
        paddingTop: 'var(--safe-area-top, 0px)',
        paddingBottom: 'var(--safe-area-bottom, 0px)'
      } : {
        bottom: '96px',
        width: `${chatSize.width}px`,
        height: `${chatSize.height}px`,
        maxHeight: 'calc(100vh - 120px)',
        maxWidth: 'calc(100vw - 48px)',
        minWidth: '320px',
        minHeight: '400px'
      }}
    >
      {/* Resize Handles (nur Desktop) */}
      {!isMobile && (
        <>
          {/* Top-Left Corner - Hauptgriff */}
          <div
            className="absolute -top-1 -left-1 w-5 h-5 cursor-nw-resize z-10 group"
            onMouseDown={startResize('top-left')}
          >
            <div className="absolute top-1.5 left-1.5 w-3 h-3">
              <GripVertical className="w-3 h-3 text-gray-300 group-hover:text-primary transition-colors transform -rotate-45" />
            </div>
          </div>
          {/* Top Edge */}
          <div
            className="absolute -top-1 left-5 right-4 h-3 cursor-n-resize z-10 group"
            onMouseDown={startResize('top')}
          >
            <div className="absolute inset-x-0 top-1 h-1 bg-transparent group-hover:bg-primary/20 rounded transition-colors" />
          </div>
          {/* Left Edge */}
          <div
            className="absolute top-5 -left-1 w-3 bottom-4 cursor-w-resize z-10 group"
            onMouseDown={startResize('left')}
          >
            <div className="absolute inset-y-0 left-1 w-1 bg-transparent group-hover:bg-primary/20 rounded transition-colors" />
          </div>
        </>
      )}

      {/* Header - WhatsApp-Style auf Mobile */}
      <div className={`bg-primary text-white px-4 flex items-center justify-between shrink-0 ${
        isMobile ? 'py-4' : 'py-3'
      }`}>
        {showHistory ? (
          <>
            <button
              onClick={() => setShowHistory(false)}
              className="p-1 hover:bg-white/20 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="font-medium">Verlauf</span>
            <div className="w-6" />
          </>
        ) : (
          <>
            <div className="flex items-center gap-3">
              {/* Zurück-Button nur auf Mobile */}
              {isMobile && (
                <button
                  onClick={onClose}
                  className="p-1 -ml-1 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
              )}
              <div className={`${isMobile ? 'w-10 h-10' : 'w-8 h-8'} bg-white/20 rounded-full flex items-center justify-center`}>
                <Bot className={isMobile ? 'w-5 h-5' : 'w-4 h-4'} />
              </div>
              <div>
                <div className={`font-medium ${isMobile ? 'text-base' : 'text-sm'}`}>
                  {user?.organisation?.orgType === 'HEYKIKI' ? 'HeyKiki - Assistent' : 'WerkPilot - KI'}
                </div>
                <div className={`text-white/70 ${isMobile ? 'text-sm' : 'text-xs'}`}>{firmenname}</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => { setSuchQuery(''); setSuchErgebnisse(null); setShowHistory(true); }}
                className={`hover:bg-white/20 rounded-lg transition-colors ${isMobile ? 'p-2.5' : 'p-2'}`}
                title="Verlauf"
              >
                <MessageSquare className={isMobile ? 'w-5 h-5' : 'w-4 h-4'} />
              </button>
              {!isMobile && (
                <button
                  onClick={resetSize}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                  title="Fenstergröße zurücksetzen"
                >
                  <Minimize2 className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={startNewConversation}
                className={`hover:bg-white/20 rounded-lg transition-colors ${isMobile ? 'p-2.5' : 'p-2'}`}
                title="Neue Unterhaltung"
              >
                <Plus className={isMobile ? 'w-5 h-5' : 'w-4 h-4'} />
              </button>
            </div>
          </>
        )}
      </div>

      {/* Content Area */}
      {showHistory ? (
        /* Conversation History */
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Suchfeld und Alle-löschen Header */}
          <div className={`${isMobile ? 'p-4' : 'p-3'} pb-0 space-y-2`}>
            <div className="relative">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 ${isMobile ? 'w-5 h-5' : 'w-4 h-4'} text-gray-400`} />
              <input
                type="text"
                value={suchQuery}
                onChange={(e) => handleSuchChange(e.target.value)}
                placeholder="Chats durchsuchen..."
                className={`w-full ${isMobile ? 'pl-10 pr-10 py-3 text-base' : 'pl-9 pr-8 py-2 text-sm'} border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-white`}
              />
              {suchQuery && (
                <button
                  onClick={() => { setSuchQuery(''); setSuchErgebnisse(null); }}
                  className={`absolute right-2 top-1/2 -translate-y-1/2 ${isMobile ? 'p-1.5' : 'p-0.5'} text-gray-400 hover:text-gray-600`}
                >
                  <X className={isMobile ? 'w-5 h-5' : 'w-3.5 h-3.5'} />
                </button>
              )}
            </div>
            {konversationen.length > 0 && (
              <button
                onClick={handleAlleLoeschen}
                className={`flex items-center gap-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors w-full ${isMobile ? 'text-sm px-3 py-2.5' : 'text-xs px-2 py-1.5'}`}
              >
                <Trash2 className={isMobile ? 'w-4 h-4' : 'w-3.5 h-3.5'} />
                Alle Chats löschen
              </button>
            )}
          </div>

          {/* Konversationsliste */}
          <div className={`flex-1 overflow-y-auto ${isMobile ? 'p-4' : 'p-3'}`}>
            {istSuche ? (
              <div className="text-center text-gray-400 py-8">
                <Loader2 className={`${isMobile ? 'w-6 h-6' : 'w-5 h-5'} animate-spin mx-auto mb-2`} />
                <span className={isMobile ? 'text-base' : ''}>Suche...</span>
              </div>
            ) : suchErgebnisse !== null ? (
              suchErgebnisse.length === 0 ? (
                <div className={`text-center text-gray-500 py-8 ${isMobile ? 'text-base' : ''}`}>
                  Keine Ergebnisse für &quot;{suchQuery}&quot;
                </div>
              ) : (
                <div className={isMobile ? 'space-y-3' : 'space-y-2'}>
                  {suchErgebnisse.map(k => (
                    <div
                      key={k.id}
                      onClick={() => loadKonversation(k.id)}
                      className={`rounded-xl bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors group ${isMobile ? 'p-4 active:bg-gray-200' : 'p-3'}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className={`font-medium text-gray-900 truncate ${isMobile ? 'text-base' : 'text-sm'}`}>
                            {k.titel || 'Unterhaltung'}
                          </div>
                          {k.letzteNachricht && (
                            <div className={`text-gray-500 mt-1 line-clamp-2 ${isMobile ? 'text-sm' : 'text-xs'}`}>
                              {k.letzteNachricht}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={(e) => deleteKonversation(k.id, e)}
                          className={`text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all shrink-0 ${isMobile ? 'p-2.5 opacity-100' : 'p-1.5 opacity-0 group-hover:opacity-100'}`}
                          title="Löschen"
                        >
                          <Trash2 className={isMobile ? 'w-5 h-5' : 'w-4 h-4'} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : konversationen.length === 0 ? (
              <div className={`text-center text-gray-500 py-8 ${isMobile ? 'text-base' : ''}`}>
                Keine bisherigen Unterhaltungen
              </div>
            ) : (
              <div className={isMobile ? 'space-y-3' : 'space-y-2'}>
                {konversationen.map(k => (
                  <div
                    key={k.id}
                    onClick={() => loadKonversation(k.id)}
                    className={`rounded-xl bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors group ${isMobile ? 'p-4 active:bg-gray-200' : 'p-3'}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className={`font-medium text-gray-900 truncate ${isMobile ? 'text-base' : 'text-sm'}`}>
                          {k.titel || 'Unterhaltung'}
                        </div>
                        {k.letzteNachricht && (
                          <div className={`text-gray-500 truncate mt-1 ${isMobile ? 'text-sm' : 'text-xs'}`}>
                            {k.letzteNachricht}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={(e) => deleteKonversation(k.id, e)}
                        className={`text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all shrink-0 ${isMobile ? 'p-2.5 opacity-100' : 'p-1.5 opacity-0 group-hover:opacity-100'}`}
                        title="Löschen"
                      >
                        <Trash2 className={isMobile ? 'w-5 h-5' : 'w-4 h-4'} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Chat Messages */
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 && (
            <div className={`text-center ${isMobile ? 'py-12' : 'py-8'}`}>
              {user?.organisation?.orgType === 'HEYKIKI' ? (
                <div className={`${isMobile ? 'w-20 h-20' : 'w-16 h-16'} rounded-full overflow-hidden mx-auto mb-4 border-2 border-[#81c264]`}>
                  <img src="/heykiki-chat-logo.png" alt="HeyKiki" className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className={`${isMobile ? 'w-20 h-20' : 'w-16 h-16'} bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4`}>
                  <Bot className={`${isMobile ? 'w-10 h-10' : 'w-8 h-8'} text-primary`} />
                </div>
              )}
              <p className={`text-gray-600 font-medium ${isMobile ? 'text-lg' : ''}`}>Hallo! Wie kann ich helfen?</p>
              <p className={`text-gray-400 mt-2 ${isMobile ? 'text-base' : 'text-sm'}`}>
                Frag mich nach Kunden, Terminen, Rechnungen...
              </p>
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                {[
                  'Zeig offene Anfragen',
                  'Termine heute',
                  'Neuen Kunden anlegen'
                ].map((suggestion, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      setInputValue(suggestion);
                      inputRef.current?.focus();
                    }}
                    className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full text-xs transition-colors"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
              {/* Powered by OpenAI */}
              <div className="flex items-center justify-center gap-2 mt-6 text-xs text-gray-400">
                <span>Powered by</span>
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z"/>
                </svg>
                <span className="font-medium">OpenAI</span>
              </div>
            </div>
          )}

          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`rounded-2xl ${
                  msg.role === 'user'
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-800'
                } ${isMobile ? 'max-w-[80%] px-4 py-3' : 'max-w-[85%] px-4 py-2.5'}`}
              >
                {/* Bild anzeigen falls vorhanden (live oder Thumbnail aus Historie) */}
                {msg.imageUrl && (
                  <div className="mb-2">
                    <img
                      src={msg.imageUrl}
                      alt="Hochgeladenes Bild"
                      className="rounded-lg max-h-48 max-w-full object-contain cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => setLightboxImage(msg.imageUrl)}
                    />
                  </div>
                )}
                {/* Thumbnails aus Historie (Geister-Bilder) */}
                {!msg.imageUrl && msg.imageThumbnails && msg.imageThumbnails.length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-1">
                    {msg.imageThumbnails.map((thumb, idx) => (
                      <div
                        key={idx}
                        className="relative"
                        title="Bild aus vorheriger Sitzung"
                      >
                        {thumb.type === 'pdf' ? (
                          <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                            <FileText className="w-6 h-6 text-red-500" />
                          </div>
                        ) : thumb.thumbnail ? (
                          <img
                            src={`data:image/jpeg;base64,${thumb.thumbnail}`}
                            alt={`Bild ${idx + 1}`}
                            className="w-12 h-12 rounded-lg object-cover opacity-70 border border-gray-300"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                            <ImageIcon className="w-6 h-6 text-gray-400" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {/* KI-generiertes Bild anzeigen (von bild_bearbeiten Tool) */}
                {msg.generatedImageUrl && (
                  <div className="mb-2">
                    <div className="relative group">
                      <div className="flex items-center gap-1.5 mb-1.5 text-xs text-purple-600 font-medium">
                        <Sparkles className="w-3.5 h-3.5" />
                        <span>KI-generiertes Bild</span>
                      </div>
                      <img
                        src={msg.generatedImageUrl}
                        alt="KI-bearbeitetes Bild"
                        className="rounded-lg max-h-64 max-w-full object-contain cursor-pointer hover:opacity-90 transition-opacity border border-purple-200"
                        onClick={() => setLightboxImage(msg.generatedImageUrl)}
                      />
                      <button
                        className="absolute bottom-2 right-2 bg-white/90 hover:bg-white text-gray-700 p-1.5 rounded-lg shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Bild herunterladen"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownloadImage(msg.generatedImageUrl, 'ki-bearbeitet.jpg');
                        }}
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
                <div className={`whitespace-pre-wrap break-words ${isMobile ? 'text-base' : 'text-sm'}`}>
                  {formatContent(msg.content)}
                </div>
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="flex justify-start">
              <div className={`bg-gray-100 rounded-2xl ${isMobile ? 'px-4 py-3' : 'px-4 py-3'}`}>
                <div className="flex items-center gap-2">
                  {loadingText === 'Vision AI lädt...' ? (
                    <Sparkles className={`animate-pulse text-purple-500 ${isMobile ? 'w-5 h-5' : 'w-4 h-4'}`} />
                  ) : (
                    <Loader2 className={`animate-spin text-primary ${isMobile ? 'w-5 h-5' : 'w-4 h-4'}`} />
                  )}
                  <span className={`${loadingText === 'Vision AI lädt...' ? 'text-purple-500' : 'text-gray-500'} ${isMobile ? 'text-base' : 'text-sm'}`}>{loadingText}</span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      )}

      {/* Input Area */}
      {!showHistory && (
        <div
          className="border-t border-gray-100 p-3 shrink-0 bg-white"
          style={isMobile ? {
            paddingBottom: 'calc(12px + var(--safe-area-bottom, 0px))'
          } : {}}
        >
          {/* Hidden File Input for Images and PDFs */}
          <input
            type="file"
            ref={imageInputRef}
            onChange={handleImageSelect}
            accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif,application/pdf"
            className="hidden"
          />

          {/* Image Error Message */}
          {imageError && (
            <div className="mb-2 px-3 py-2 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm flex items-center gap-2">
              <ImageIcon className="w-4 h-4 shrink-0" />
              <span>{imageError}</span>
            </div>
          )}

          {/* Speech Error Message */}
          {speechError && (
            <div className="mb-2 px-3 py-2 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm flex items-center gap-2">
              <MicOff className="w-4 h-4 shrink-0" />
              <span>{speechError}</span>
            </div>
          )}

          {/* File Preview (Image or PDF) */}
          {selectedImage && (
            <div className="mb-2 relative inline-block">
              {selectedImage.isPdf ? (
                /* PDF Preview */
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl">
                  <FileText className="w-8 h-8 text-red-500" />
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-700 truncate max-w-[200px]">
                      {selectedImage.filename || 'PDF-Dokument'}
                    </span>
                    <span className="text-xs text-gray-500">PDF-Datei</span>
                  </div>
                </div>
              ) : (
                /* Image Preview */
                <img
                  src={selectedImage.preview}
                  alt="Vorschau"
                  className="rounded-xl max-h-32 max-w-full object-contain border border-gray-200"
                />
              )}
              {isUploadingImage && (
                <div className="absolute inset-0 bg-black/50 rounded-xl flex items-center justify-center">
                  <Loader2 className="w-6 h-6 text-white animate-spin" />
                </div>
              )}
              <button
                onClick={clearSelectedImage}
                disabled={isUploadingImage}
                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow-lg"
                title={selectedImage.isPdf ? 'PDF entfernen' : 'Bild entfernen'}
              >
                <X className="w-4 h-4" />
              </button>
              {uploadedImageId && !isUploadingImage && (
                <div className="absolute bottom-1 right-1 bg-green-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                  Bereit
                </div>
              )}
            </div>
          )}

          {/* Recording Indicator with Live Transcription */}
          {isRecording && (
            <div className="mb-2 px-3 py-2 bg-red-50 border border-red-200 rounded-xl">
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1">
                  <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                  <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }} />
                  <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }} />
                </div>
                <span className="text-red-600 text-sm font-medium">Aufnahme läuft...</span>
              </div>
              {interimTranscript && (
                <div className="mt-1 text-gray-600 text-sm italic">
                  "{interimTranscript}"
                </div>
              )}
            </div>
          )}

          <div className="flex items-end gap-2">
            {/* Camera Button */}
            <button
              onClick={openImagePicker}
              disabled={isLoading || isRecording || isUploadingImage}
              className={`rounded-full transition-colors ${
                selectedImage
                  ? 'bg-primary/20 text-primary'
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              } ${isMobile ? 'p-3' : 'p-2.5'}`}
              title="Bild hinzufügen"
            >
              <Camera className={isMobile ? 'w-6 h-6' : 'w-5 h-5'} />
            </button>

            <div className="flex-1 relative">
              <textarea
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                placeholder={isRecording ? 'Sprechen Sie jetzt...' : selectedImage ? 'Frage zum Bild...' : 'Nachricht eingeben...'}
                className={`w-full px-4 pr-10 bg-white border rounded-3xl resize-none focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                  isRecording ? 'border-red-300 bg-red-50/30' : 'border-gray-200'
                } ${isMobile ? 'py-3 text-base max-h-40' : 'py-2.5 text-sm max-h-32'}`}
                rows={1}
                disabled={isLoading || isRecording}
                style={{
                  minHeight: isMobile ? '48px' : '44px',
                  height: 'auto'
                }}
                onInput={(e) => {
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, isMobile ? 160 : 128) + 'px';
                }}
              />
            </div>

            {/* Voice Button */}
            {speechSupported && (
              <button
                onClick={toggleRecording}
                disabled={isLoading || isUploadingImage}
                className={`rounded-full transition-all duration-200 ${
                  isRecording
                    ? 'bg-red-500 text-white shadow-lg shadow-red-500/30 scale-110'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                } ${isMobile ? 'p-3' : 'p-2.5'}`}
                title={isRecording ? 'Aufnahme stoppen' : 'Spracheingabe'}
              >
                {isRecording ? (
                  <div className="relative">
                    <MicOff className={isMobile ? 'w-6 h-6' : 'w-5 h-5'} />
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-white rounded-full animate-ping" />
                  </div>
                ) : (
                  <Mic className={isMobile ? 'w-6 h-6' : 'w-5 h-5'} />
                )}
              </button>
            )}

            {/* Send Button */}
            <button
              data-chat-send
              onClick={sendMessage}
              disabled={(!inputValue.trim() && !uploadedImageId) || isLoading || isRecording || isUploadingImage}
              className={`rounded-full transition-colors ${
                (inputValue.trim() || uploadedImageId) && !isLoading && !isRecording && !isUploadingImage
                  ? 'bg-primary text-white hover:bg-primary-dark'
                  : 'bg-gray-200 text-gray-400'
              } ${isMobile ? 'p-3' : 'p-2.5'}`}
              title="Senden"
            >
              {isLoading ? (
                <Loader2 className={isMobile ? 'w-6 h-6 animate-spin' : 'w-5 h-5 animate-spin'} />
              ) : (
                <Send className={isMobile ? 'w-6 h-6' : 'w-5 h-5'} />
              )}
            </button>
          </div>
        </div>
      )}
      {/* Lightbox Overlay */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-[9999] bg-black bg-opacity-95 flex items-center justify-center"
          onClick={() => setLightboxImage(null)}
        >
          <button
            onClick={() => setLightboxImage(null)}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
            title="Schließen"
          >
            <X className="w-8 h-8" />
          </button>
          <button
            className="absolute bottom-6 right-6 bg-white/20 hover:bg-white/40 text-white p-3 rounded-full transition-colors z-10"
            title="Herunterladen"
            onClick={(e) => {
              e.stopPropagation();
              handleDownloadImage(lightboxImage, 'bild.jpg');
            }}
          >
            <Download className="w-6 h-6" />
          </button>
          <img
            src={lightboxImage}
            alt="Vollbild"
            className="max-w-[95vw] max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
      {showChatSignature && (
        <SignaturePad
          onSave={handleChatSignatureSave}
          onClose={() => setShowChatSignature(null)}
        />
      )}
    </div>
  );
}
